import { useCallback, useEffect, useRef, useState } from "react";

// A slim, always-visible, click-and-drag horizontal scrollbar rendered
// ABOVE a scrollable table (owner ask) — the native scrollbar sits at the
// BOTTOM of the table's own overflow-x-auto container, which is easy to
// lose track of once a table has many rows, and dragging it at all needs a
// trackpad or shift+wheel on some setups. This mirrors the real container's
// scrollLeft/scrollWidth/clientWidth and drives it directly, so it's a
// second way to reach the same scroll position, not a separate one.
//
// Pointer-capture + rAF-throttled drag, same interaction style as
// ColumnResizeHandle.jsx/FloatingWindow.jsx elsewhere in this app.
export default function TableScrollbar({ scrollRef, scrollTargetId }) {
  const [metrics, setMetrics] = useState({ scrollWidth: 0, clientWidth: 0, scrollLeft: 0 });
  const trackRef = useRef(null);
  const gesture = useRef(null);
  const frame = useRef(null);
  const [dragging, setDragging] = useState(false);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setMetrics({ scrollWidth: el.scrollWidth, clientWidth: el.clientWidth, scrollLeft: el.scrollLeft });
  }, [scrollRef]);

  // Re-measure on scroll (dragged the native bottom scrollbar, or a
  // trackpad/shift-wheel), on the container resizing, and on the TABLE
  // inside it resizing — a column resize changes scrollWidth without the
  // wrapper's own box changing size at all.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
    };
  }, [scrollRef, measure]);

  const { scrollWidth, clientWidth, scrollLeft } = metrics;
  // +1 avoids a flicker right at the boundary from sub-pixel rounding.
  const canScroll = scrollWidth > clientWidth + 1;
  const maxScrollLeft = scrollWidth - clientWidth;
  const thumbWidthPct = canScroll ? Math.max((clientWidth / scrollWidth) * 100, 4) : 100;

  const onThumbPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    gesture.current = { startX: e.clientX, startScrollLeft: scrollLeft };
    setDragging(true);
  };

  const onThumbPointerMove = (e) => {
    const g = gesture.current;
    if (!g || !trackRef.current) return;
    if (frame.current) return;
    const { clientX } = e;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      const trackWidth = trackRef.current.getBoundingClientRect().width;
      const draggableWidth = trackWidth * (1 - thumbWidthPct / 100);
      const dx = clientX - g.startX;
      const ratio = draggableWidth > 0 ? dx / draggableWidth : 0;
      const next = Math.min(Math.max(g.startScrollLeft + ratio * maxScrollLeft, 0), maxScrollLeft);
      if (scrollRef.current) scrollRef.current.scrollLeft = next;
    });
  };

  const endGesture = (e) => {
    if (!gesture.current) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    gesture.current = null;
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
    setDragging(false);
  };

  // Same reasoning as ColumnResizeHandle: the pointer regularly crosses
  // over page text mid-drag.
  useEffect(() => {
    if (!dragging) return;
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = prev;
    };
  }, [dragging]);

  useEffect(() => () => frame.current && cancelAnimationFrame(frame.current), []);

  if (!canScroll) return null;

  const thumbLeftPct = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * (100 - thumbWidthPct) : 0;

  return (
    <div ref={trackRef} className="relative h-2 mb-1 rounded-full bg-surface-inset">
      <div
        onPointerDown={onThumbPointerDown}
        onPointerMove={onThumbPointerMove}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
        role="scrollbar"
        aria-orientation="horizontal"
        aria-controls={scrollTargetId}
        aria-valuemin={0}
        aria-valuemax={maxScrollLeft}
        aria-valuenow={scrollLeft}
        className={`absolute top-0 bottom-0 rounded-full cursor-grab active:cursor-grabbing touch-none select-none ${
          dragging ? "bg-brand-500" : "bg-zinc-400 hover:bg-brand-400"
        }`}
        style={{ width: `${thumbWidthPct}%`, left: `${thumbLeftPct}%` }}
      />
    </div>
  );
}
